import type { DirectorStore } from "services/director/directorStore";
import type { IssuesStore } from "services/issues/issuesStore";
import type { PricingPlanStore } from "services/pricingPlan/gb/pricingPlanStore";
import { QuestionsStore } from "services/questions/questionsStore";
import { PARTICULARS_QUESTION_TO_PRODUCT, ParticularsQuestion } from "./particularsConstants";

export class ParticularQuestionStore extends QuestionsStore<ParticularsQuestion> {
  constructor(
    private readonly _pricingPlanStore: PricingPlanStore,
    private readonly _directorStore: DirectorStore,
    protected readonly _issuesStore: IssuesStore,
  ) {
    super(ParticularsQuestion, _issuesStore);
  }

  // This function returns a ParticularsQuestion if the company has a totalSharesNum or totalCapitalGbp
  protected _getRelevantQuestion(): ParticularsQuestion | undefined {
    const { directors } = this._directorStore;

    const isShareHolderMissingShares =
      directors.filter((director) => !director.ownsSharesNum && director.isShareholder).length > 0;

    const totalShares = directors.reduce((curr, acc) => curr + (acc?.ownsSharesNum ?? 0), 0);

    if (isShareHolderMissingShares || (directors.length > 1 && totalShares < 100)) {
      return ParticularsQuestion.Particulars;
    }

    // check if isPersonaSignificantControl is undefined
    const isPersonaSignificantControl = directors.some(
      (director) => director.isPersonWithSignificantControl === undefined,
    );

    const isPscMissingNatureOfControl = directors.some(
      (director) =>
        director.isPersonWithSignificantControl === true &&
        (director.hasRightToAppointNewDirectors === undefined ||
          director.hasSignificantControlOutsideOfShares === undefined),
    );

    if (directors.length > 1 && (isPersonaSignificantControl || isPscMissingNatureOfControl)) {
      return ParticularsQuestion.ConfirmAppointments;
    }

    if (isPersonaSignificantControl || isPscMissingNatureOfControl) {
      return ParticularsQuestion.Particulars;
    }

    return undefined;
  }

  protected _shouldSkipQuestion(question: ParticularsQuestion): boolean {
    if (question === ParticularsQuestion.SharesManagement) {
      return true;
    }

    if (question === ParticularsQuestion.ConfirmAppointments) {
      return this._directorStore.directors.length <= 1;
    }

    const product = PARTICULARS_QUESTION_TO_PRODUCT[question];

    if (product && this._pricingPlanStore.isProductIncluded(product)) {
      return true;
    }

    return super._shouldSkipQuestion(question);
  }
}
